import { Affix, Box, Button, Select, TextField } from '@warp-ds/react';
import { useState } from 'react';
import { currencySymbols } from '@/utils/symbols';

import type { CurrencyData } from '../../interfaces';

const mostUsedCurrencies = ['NOK', 'EUR', 'USD', 'GBP', 'DKK', 'SEK', 'THB'];

export type Props = Pick<CurrencyData, 'data'>;
export const CurrencyCalculator = ({ data }: Props) => {
    const calculateExchange = (from: string, to: string, amount: number): string => {
        if (!data) return '0';
        const fromCurrency = data[from];
        const toCurrency = data[to];
        const inNOK = amount * (fromCurrency.rate / fromCurrency.unit);
        return (inNOK / (toCurrency.rate / toCurrency.unit)).toFixed(3);
    };

    const [leftValue, setLeftValue] = useState(data ? '100' : '0');
    const [rightValue, setRightValue] = useState(calculateExchange('NOK', 'EUR', 100));
    const [leftSelect, setLeftSelect] = useState('NOK');
    const [rightSelect, setRightSelect] = useState('EUR');

    const handleLeftInput = (value: string) => {
        setLeftValue(value);
        setRightValue(calculateExchange(leftSelect, rightSelect, Number(value)));
    };
    const handleRightInput = (value: string) => {
        setRightValue(value);
        setLeftValue(calculateExchange(rightSelect, leftSelect, Number(value)));
    };

    const handleLeftSelect = (currency: string) => {
        setLeftSelect(currency);
        setLeftValue(calculateExchange(rightSelect, currency, Number(rightValue)));
    };
    const handleRightSelect = (currency: string) => {
        setRightSelect(currency);
        setRightValue(calculateExchange(leftSelect, currency, Number(leftValue)));
    };

    const handleSwap = () => {
        const rightVal = rightValue;
        const rightSel = rightSelect;
        setRightValue(leftValue);
        setRightSelect(leftSelect);
        setLeftValue(rightVal);
        setLeftSelect(rightSel);
    };

    const renderInput = (id: string, value: string, currency: string, onChange: (newValue: string) => void) => (
        <TextField
            type="number"
            aria-label={id === 'left' ? 'Fra beløp' : 'Til beløp'}
            data-testid={`currency-input-${id}`}
            lang="nb"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            disabled={!data}
            className="mb-12">
            <Affix suffix label={currencySymbols[currency] ?? currency} />
        </TextField>
    );

    const notMostUsedCurrencies = Object.keys(data ?? {})
        .filter((currency) => !mostUsedCurrencies.includes(currency))
        .sort((a, b) => (data[a].name > data[b].name ? 1 : -1));

    const renderSelect = (id: string, value: string, onChange: (newValue: string) => void) => (
        <Select
            aria-label={id === 'left' ? 'Fra valuta' : 'Til valuta'}
            data-testid={`currency-select-${id}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={!data}>
            {data ? (
                <>
                    <optgroup label="Mest brukte valuta">
                        {mostUsedCurrencies.map((currency) => (
                            <option value={currency} key={id + currency}>
                                {data[currency]?.name} ({currency})
                            </option>
                        ))}
                    </optgroup>
                    <optgroup label="Øvrige valuta">
                        {notMostUsedCurrencies.map((currency) => (
                            <option value={currency} key={id + currency}>
                                {data[currency]?.name} ({currency})
                            </option>
                        ))}
                    </optgroup>
                </>
            ) : (
                <option value={value}>{value}</option>
            )}
        </Select>
    );

    return (
        <Box info className="sm:p-32 grid" style={{ gridTemplateColumns: '1fr auto 1fr' }}>
            <div>
                {renderInput('left', leftValue, leftSelect, handleLeftInput)}
                {renderSelect('left', leftSelect, handleLeftSelect)}
            </div>
            <div className="w-40 sm:w-64">
                <Button quiet aria-label="Bytt fra-til" className="block mt-28 mx-auto px-4 sm:px-16" onClick={handleSwap}>
                    <svg width="21" height="18" viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M15.349 8.65642C15.0233 8.92781 14.5289 8.69623 14.5289 8.27231V5.73983H3.70016C3.03742 5.73983 2.50016
                            5.20257 2.50016 4.53983C2.50016 3.87709 3.03742 3.33983 3.70016 3.33983H14.5289V0.807352C14.5289 0.383433
                            15.0233 0.151855 15.349 0.423242L19.828 4.15572C20.0679 4.35562 20.0679 4.72404 19.828 4.92394L15.349
                            8.65642ZM4.91132 9.44341C5.23699 9.17202 5.73142 9.4036 5.73142 9.82752V12.36H16.3981C17.0609 12.36 17.5981
                            12.8973 17.5981 13.56C17.5981 14.2227 17.0609 14.76 16.3981 14.76H5.73142V17.2925C5.73142 17.7164 5.23699
                            17.948 4.91132 17.6766L0.432349 13.9441C0.192472 13.7442 0.192472 13.3758 0.432349 13.1759L4.91132 9.44341Z"
                            fill="#767676"
                        />
                    </svg>
                </Button>
            </div>
            <div>
                {renderInput('right', rightValue, rightSelect, handleRightInput)}
                {renderSelect('right', rightSelect, handleRightSelect)}
            </div>
        </Box>
    );
};
